// Service to fetch motor insurance quotations and filter options
const motorPrivateService = {
	// Fetches quotations based on cover type and vehicle value
	async getQuotations(coverType, vehicleValue) {
		if (!['comprehensive', 'third-party'].includes(coverType)) {
			throw new Error('Invalid cover type');
		}
		if (
			coverType === 'comprehensive' &&
			(!vehicleValue || vehicleValue <= 0)
		) {
			throw new Error(
				'Vehicle value must be greater than 0 for comprehensive cover',
			);
		}

		// Simulate API delay
		await new Promise((resolve) => setTimeout(resolve, 1000));

		if (coverType === 'comprehensive') {
			return [
				{
					id: 101,
					name: 'Comprehensive Elite',
					premium: vehicleValue * 0.04,
					underwriter: 'Jubilee Insurance',
					intermediary: 'Direct',
					features: [
						'Accidental Damage',
						'Theft',
						'Third Party Liability',
						'Personal Accident',
						'Enhanced Medical Cover',
					],
					description:
						'Premium comprehensive package with extensive protection and benefits.',
					benefits: [
						'Full accidental damage cover',
						'Nil excess on windscreen claims',
						'Courtesy car for up to 14 days',
						'Enhanced personal accident cover',
					],
					termsAndConditions:
						'Terms & Conditions for Comprehensive Elite...',
					coreBenefits: [
						{
							id: 'cb1',
							name: 'Third Party Liability',
							description:
								'Covers legal liability for third party injury or property damage',
							limits: [
								{ value: 0, premium: 0 },
								{ value: 5000000, premium: 0 },
								{ value: 10000000, premium: 0 },
							],
							selectedLimit: 5000000,
						},
						{
							id: 'cb2',
							name: 'Own Damage',
							description: 'Covers damage to your own vehicle',
							limits: [
								{ value: 0, premium: 0 },
								{ value: vehicleValue * 0.5, premium: 0 },
								{ value: vehicleValue, premium: 0 },
							],
							selectedLimit: vehicleValue * 0.5,
						},
						{
							id: 'cb3',
							name: 'Fire & Theft',
							description: 'Covers loss due to fire or theft',
							limits: [
								{ value: 0, premium: 0 },
								{ value: vehicleValue * 0.5, premium: 0 },
								{ value: vehicleValue, premium: 0 },
							],
							selectedLimit: vehicleValue * 0.5,
						},
					],
					optionalBenefits: [
						{
							id: 'ob1',
							name: 'Excess Protector',
							premium: 2500,
							description:
								'Covers the excess amount in case of a claim',
							limits: [
								{ value: 25000, premium: 2500 },
								{ value: 50000, premium: 4500 },
								{ value: 75000, premium: 6000 },
							],
							selectedLimit: 25000,
						},
						{
							id: 'ob2',
							name: 'Political Violence & Terrorism',
							premium: 3500,
							description:
								'Coverage against political risks and terrorism',
							limits: [
								{ value: 1000000, premium: 3500 },
								{ value: 2000000, premium: 6000 },
								{ value: 3000000, premium: 8000 },
							],
							selectedLimit: 1000000,
						},
						{
							id: 'ob3',
							name: 'Loss of Use',
							premium: 2000,
							description:
								'Provides alternative transport when vehicle is under repair',
						},
						{
							id: 'ob4',
							name: 'Windscreen Cover',
							premium: 1500,
							description:
								'Covers windscreen and window glass damage',
						},
					],
				},
				{
					id: 102,
					name: 'Comprehensive Plus',
					premium: vehicleValue * 0.035,
					underwriter: 'APA Insurance',
					intermediary: 'PineBridge',
					features: [
						'Accidental Damage',
						'Theft',
						'Third Party Liability',
						'Standard Medical Cover',
					],
					description:
						'Popular comprehensive plan balancing coverage and cost.',
					benefits: [
						'Full accidental damage cover',
						'Third party property and bodily injury liability',
						'Fire and theft cover',
						'Standard personal accident cover',
					],
					termsAndConditions:
						'Terms & Conditions for Comprehensive Plus...',
					coreBenefits: [
						{
							id: 'cb1',
							name: 'Third Party Liability',
							description:
								'Covers legal liability for third party injury or property damage',
							limits: [
								{ value: 0, premium: 0 },
								{ value: 5000000, premium: 0 },
								{ value: 10000000, premium: 0 },
							],
							selectedLimit: 5000000,
						},
						{
							id: 'cb2',
							name: 'Own Damage',
							description: 'Covers damage to your own vehicle',
							limits: [
								{ value: 0, premium: 0 },
								{ value: vehicleValue * 0.5, premium: 0 },
								{ value: vehicleValue, premium: 0 },
							],
							selectedLimit: vehicleValue * 0.5,
						},
						{
							id: 'cb3',
							name: 'Fire & Theft',
							description: 'Covers loss due to fire or theft',
							limits: [
								{ value: 0, premium: 0 },
								{ value: vehicleValue * 0.5, premium: 0 },
								{ value: vehicleValue, premium: 0 },
							],
							selectedLimit: vehicleValue * 0.5,
						},
					],
					optionalBenefits: [
						{
							id: 'ob1',
							name: 'Excess Protector',
							premium: 2500,
							description:
								'Covers the excess amount in case of a claim',
							limits: [
								{ value: 25000, premium: 2500 },
								{ value: 50000, premium: 4500 },
							],
							selectedLimit: 25000,
						},
						{
							id: 'ob3',
							name: 'Loss of Use',
							premium: 2000,
							description:
								'Provides alternative transport when vehicle is under repair',
						},
					],
				},
			];
		}

		return [
			{
				id: 201,
				name: 'Third Party Only',
				premium: 20000,
				underwriter: 'Jubilee Insurance',
				intermediary: 'Direct',
				features: [
					'Third Party Bodily Injury',
					'Third Party Property Damage',
				],
				description:
					'Mandatory minimum cover for third party liability.',
				benefits: [
					'Covers legal liability for third party death or injury',
					'Covers legal liability for third party property damage',
				],
				termsAndConditions:
					'Terms & Conditions for Third Party Only...',
				coreBenefits: [
					{
						id: 'cb1',
						name: 'Third Party Liability',
						description:
							'Covers legal liability for third party injury or property damage',
						limits: [
							{ value: 0, premium: 0 },
							{ value: 5000000, premium: 0 },
							{ value: 10000000, premium: 0 },
						],
						selectedLimit: 5000000,
					},
				],
				optionalBenefits: [],
			},
		];
	},

	// Returns filter options for quotations
	async getFilterOptions() {
		return {
			underwriters: [
				'All',
				'Jubilee Insurance',
				'APA Insurance',
				'CIC Insurance',
			],
			intermediaries: ['All', 'Direct', 'PineBridge'],
			productTypes: ['All', 'Comprehensive', 'Third Party'],
		};
	},
};

export { motorPrivateService };
